import { useState, useEffect, useCallback, useContext } from "react";
import "./MindMappingDashboard.css";
import { FaArrowLeftLong, FaRegCalendar, FaUserGraduate, FaIndustry, FaLocationDot, FaBullseye, FaCompass, FaArrowUpRightFromSquare, FaTrash } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import Loader from "../components/loader";
import { UserContext } from "../context/UserContext";
import { apiFetch } from "../utils/apiFetch";

export default function MindMappingDashboard() {
  const navigate = useNavigate();
  const { user, loading: userLoading } = useContext(UserContext);
  
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [loadingResolve, setLoadingResolve] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSectorFilter, setSelectedSectorFilter] = useState("All");
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/mind-mapping/delete-report/${deleteTargetId}`;
    console.log("[DELETE CONFIRM] Fetching URL:", url);
    try {
      const res = await apiFetch(url, {
        method: "DELETE",
      });
      console.log("[DELETE CONFIRM] Response status:", res.status);
      const data = await res.json();
      console.log("[DELETE CONFIRM] Response data:", data);
      if (data.success) {
        setReports(prev => prev.filter(r => String(r.id) !== String(deleteTargetId)));
      } else {
        console.error("[DELETE CONFIRM] Deletion failed:", data.error || data.message);
        alert(`Deletion failed: ${data.error || data.message || "Unknown error"}`);
      }
    } catch (err) {
      console.error("[DELETE CONFIRM] Network error during deletion:", err);
      alert(`Network error during deletion: ${err.message}`);
    } finally {
      setDeleting(false);
      setDeleteTargetId(null);
    }
  };

  const fetchReports = useCallback(async (userId) => {
    try {
      const res = await apiFetch(
        `/api/mind-mapping/report-list/${userId}`
      );
      const data = await res.json();

      if (data?.success) {
        setReports(data.reports || []);
      } else {
        setReports([]);
      }
    } catch (err) {
      console.error("Error fetching mind mapping reports:", err);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  useEffect(() => {
    if (!userLoading && user?.user_id) {
      fetchReports(user.user_id);
    } else if (!userLoading && !user) {
      setLoadingReports(false);
    }
  }, [user, userLoading, fetchReports]);

  // Dynamic state/course/goal ID resolver
  const handleOpenDashboard = async (report) => {
    setLoadingResolve(true);
    try {
      // 1. Resolve Course ID from backend by course name
      let courseId = null;
      try {
        const resCourse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/mind-mapping/course-id/${encodeURIComponent(report.course)}`
        );
        if (resCourse.ok) {
          const dataCourse = await resCourse.json();
          courseId = dataCourse.id;
        }
      } catch (err) {
        console.error("Error resolving course ID:", err);
      }

      // 2. Resolve country/state IDs
      let countryId = 1; // Default to India (1)
      let stateId = null;
      try {
        // Resolve country ID
        const resCountries = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/countries`);
        if (resCountries.ok) {
          const countriesList = await resCountries.json();
          const matchedCountry = countriesList.find(
            (c) => c.name.toLowerCase() === report.country.toLowerCase()
          );
          if (matchedCountry) {
            countryId = matchedCountry.id;
          }
        }

        // Resolve state ID
        const resStates = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/states/${countryId}`);
        if (resStates.ok) {
          const statesList = await resStates.json();
          const matchedState = statesList.find(
            (s) => s.name.toLowerCase() === report.state.toLowerCase()
          );
          if (matchedState) {
            stateId = matchedState.id;
          }
        }
      } catch (err) {
        console.error("Error resolving state ID:", err);
      }

      // 3. Resolve Goals from Name to ID mapping
      let goalIds = [];
      try {
        const resGoals = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mind-mapping/goals`);
        if (resGoals.ok) {
          const goalsList = await resGoals.json();
          const goalMap = {};
          goalsList.forEach((g) => {
            goalMap[g.name.toLowerCase().trim()] = g.id;
          });

          goalIds = report.goals
            .map((goalName) => goalMap[goalName.toLowerCase().trim()])
            .filter(Boolean);
        }
      } catch (err) {
        console.error("Error resolving goal IDs:", err);
      }

      // Fallbacks if backend doesn't resolve
      if (!courseId) courseId = 1;
      if (!stateId) stateId = 1;
      if (!goalIds || goalIds.length !== 6) {
        // Build fallback goal IDs based on static matching
        const goalNameToId = {
          "learning & knowledge": 1,
          "technology & digital behavior": 2,
          "work & career habits": 3,
          "money & finance": 4,
          "health & wellness": 5,
          "lifestyle & daily routines": 6,
          "social & community behavior": 7,
          "entertainment & creativity": 8,
          "shopping & consumption": 9,
          "problem-solving & frustrations": 10,
          "values & beliefs": 11,
          "curiosity & exploration": 12,
        };
        goalIds = report.goals
          .map((name) => goalNameToId[name.toLowerCase().trim()])
          .filter(Boolean);

        if (goalIds.length !== 6) {
          goalIds = [1, 2, 3, 4, 5, 6]; // Final absolute fallback
        }
      }

      // 4. Save state to localStorage for MindMapSecB dashboard
      localStorage.setItem("mind-mapping-access", "true");
      localStorage.setItem("selectedSector", report.sector);
      localStorage.setItem("selectedSectorType", report.sector_type);
      localStorage.setItem("selectedState", report.state);
      localStorage.setItem("selectedDistrict", report.district);
      localStorage.setItem("course_id", courseId);
      localStorage.setItem("selectedStateId", stateId);
      localStorage.setItem("graduation_course", report.course);
      localStorage.setItem("department", Array.isArray(report.departments) ? report.departments[0] : report.departments);
      localStorage.setItem("selected_goals", JSON.stringify(goalIds));

      // 5. Navigate
      navigate(`/mind-mapping/sec-b`);
    } catch (err) {
      console.error("Failed to restore discovery dashboard session:", err);
    } finally {
      setLoadingResolve(false);
    }
  };

  // Filter & Search Logic
  const filteredReports = reports.filter((report) => {
    const matchesSearch =
      report.sector.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.course.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.district.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesSector =
      selectedSectorFilter === "All" ||
      report.sector_type.toLowerCase() === selectedSectorFilter.toLowerCase();

    return matchesSearch && matchesSector;
  });

  // Extract unique sector types
  const sectorTypes = ["All", "Primary", "Secondary", "Tertiary"];

  if (userLoading || loadingReports) {
    return (
      <div className="mm-dashboard-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="mm-dashboard-container">
      {loadingResolve && (
        <div className="resolve-overlay">
          <div className="resolve-spinner-box">
            <div className="resolve-spinner"></div>
            <p>Resolving session parameters and launching dashboard...</p>
          </div>
        </div>
      )}

      <div className="mm-dashboard-header">
        <button
          className="mm-dashboard-back-btn"
          onClick={() => navigate(-1)}
          title="Go back"
        >
          <FaArrowLeftLong size={22} />
        </button>
        <div className="mm-dashboard-title-area">
          <h2 className="mm-dashboard-heading">Startup Idea Discovery History</h2>
          <p className="mm-dashboard-subheading">
            Access your previously created Founder Fit Idea Discovery Dashboards
          </p>
        </div>
      </div>

      <div className="mm-dashboard-toolbar">
        <div className="search-box-wrapper">
          <input
            type="text"
            className="mm-dashboard-search"
            placeholder="Search by sector, course, or district..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-pills">
          {sectorTypes.map((type) => (
            <button
              key={type}
              className={`filter-pill ${selectedSectorFilter === type ? "active" : ""}`}
              onClick={() => setSelectedSectorFilter(type)}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {filteredReports.length === 0 ? (
        <div className="mm-dashboard-empty">
          <FaCompass className="compass-icon animate-pulse" size={60} />
          <p className="empty-title">No Discovery Sessions Found</p>
          <p className="empty-subtitle">
            {searchQuery || selectedSectorFilter !== "All"
              ? "Try adjusting your filters or search keywords."
              : "Generate your first discovery session using the mind mapping module."}
          </p>
        </div>
      ) : (
        <div className="mm-dashboard-grid">
          {filteredReports.map((report) => (
            <div className="mm-dash-session-card" key={report.id}>
              <div className="mm-dash-card-header">
                <div className="mm-dash-card-date-badge">
                  <FaRegCalendar size={14} />
                  <span>
                    {new Date(report.created_at).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span className={`mm-dash-sector-tag ${report.sector_type.toLowerCase()}`}>
                    {report.sector_type}
                  </span>
                  <button 
                    className="mm-dash-delete-btn"
                    onClick={() => setDeleteTargetId(report.id)}
                    title="Delete Session"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              </div>

              <div className="mm-dash-card-body">
                <h3 className="mm-dash-card-sector-title">
                  <FaIndustry size={16} className="mm-dash-card-icon" />
                  <span>{report.sector}</span>
                </h3>

                <div className="mm-dash-card-meta-item">
                  <FaUserGraduate size={14} className="mm-dash-card-icon" />
                  <span className="mm-dash-meta-text">
                    {report.course} • {Array.isArray(report.departments) ? report.departments[0] : report.departments}
                  </span>
                </div>

                <div className="mm-dash-card-meta-item">
                  <FaLocationDot size={14} className="mm-dash-card-icon" />
                  <span className="mm-dash-meta-text">
                    {report.district}, {report.state}
                  </span>
                </div>

                <div className="mm-dash-card-personality-traits">
                  <div className="mm-dash-trait-pill">
                    {report.trait_intro_extro}
                  </div>
                  <div className="mm-dash-trait-pill">
                    {report.trait_think_feel}
                  </div>
                  <div className="mm-dash-trait-pill">
                    {report.trait_judge_perceive}
                  </div>
                  <div className="mm-dash-trait-pill">
                    {report.trait_sense_intuit}
                  </div>
                </div>

                <div className="mm-dash-card-goals-section">
                  <p className="mm-dash-goals-section-title">
                    <FaBullseye size={13} /> Selected Goals
                  </p>
                  <ul className="mm-dash-goals-list-mini">
                    {report.goals.slice(0, 3).map((goal, i) => (
                      <li key={i} className="mm-dash-goal-item-mini">
                        {goal}
                      </li>
                    ))}
                    {report.goals.length > 3 && (
                      <li className="mm-dash-goal-item-mini more-count">
                        +{report.goals.length - 3} more goals
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              <div className="mm-dash-card-footer">
                <button
                  className="mm-dash-card-launch-btn"
                  onClick={() => handleOpenDashboard(report)}
                >
                  <span>Open Dashboard</span>
                  <FaArrowUpRightFromSquare size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteTargetId && (
        <div className="confirm-delete-overlay">
          <div className="confirm-delete-modal">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this discovery session? This action cannot be undone.</p>
            <div className="confirm-delete-actions">
              <button className="confirm-delete-btn" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
              <button className="confirm-cancel-btn" onClick={() => setDeleteTargetId(null)} disabled={deleting}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
